'use client';

import { useState } from 'react';
import { Save, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthModal } from '@/components/auth/auth-modal';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import {
  createSavedTemplate,
  updateSavedTemplate,
} from '@/lib/saved-templates';
import { cn } from '@/lib/utils';

type SaveMode = 'new' | 'update';

interface SaveTemplateButtonProps {
  templateName?: string;
  compact?: boolean;
}

export function SaveTemplateButton({ templateName, compact = false }: SaveTemplateButtonProps) {
  const { requireAuth, showAuthModal, setShowAuthModal } = useAuthGate();
  const template = useReceiptBuilder((s) => s.template);
  const savedTemplateId = useReceiptBuilder((s) => s.savedTemplateId);
  const markSaved = useReceiptBuilder((s) => s.markSaved);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<SaveMode>('new');
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const openDialog = (nextMode: SaveMode) => {
    setMode(nextMode);
    setName(templateName || template.name || 'My Receipt');
    setError(null);
    setOpen(true);
  };

  const handleClick = () => {
    const proceed = () => openDialog(savedTemplateId ? 'update' : 'new');
    if (!requireAuth(proceed)) return;
    proceed();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a name.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (mode === 'update' && savedTemplateId) {
        const updated = await updateSavedTemplate(savedTemplateId, {
          name: trimmed,
          data: template,
        });
        markSaved(updated.id);
      } else {
        const created = await createSavedTemplate(trimmed, template);
        markSaved(created.id);
      }
      setOpen(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const showSavedBadge = justSaved;

  return (
    <>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />

      {compact ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          className="h-8 w-8 p-0"
          title={savedTemplateId ? 'Update saved template' : 'Save template'}
        >
          {showSavedBadge ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Save className="h-3.5 w-3.5" />}
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          className={cn(
            'h-10 w-full justify-center bg-background hover:bg-muted shadow-sm',
            showSavedBadge && 'text-green-700'
          )}
        >
          {showSavedBadge ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              <span className="text-xs">Saved</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              <span className="text-xs">{savedTemplateId ? 'Update' : 'Save'}</span>
            </>
          )}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {mode === 'update' ? 'Update saved template' : 'Save template'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'update'
                ? 'Save the latest changes to this template. You can also rename it.'
                : 'Save this receipt to your account so you can open it again later.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="template-name">Name</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                autoFocus
                placeholder="My Receipt"
                disabled={isSaving}
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              {mode === 'update' && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setMode('new')}
                  disabled={isSaving}
                >
                  Save as new
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : mode === 'update' ? (
                  'Update'
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
